<script setup lang="ts">
import { Bars3Icon, XMarkIcon } from '@heroicons/vue/24/outline'
import { MARKETING_CONTACT_EMAIL, MARKETING_LEGAL_LINKS, MARKETING_NAVIGATION, MARKETING_ROUTES } from '~~/shared/config/marketing'

const route = useRoute()
const isMenuOpen = ref(false)

function closeMenu(): void {
  isMenuOpen.value = false
}

function handleEscape(event: KeyboardEvent): void {
  if (event.key === 'Escape' && isMenuOpen.value) {
    closeMenu()
  }
}

onMounted(() => window.addEventListener('keydown', handleEscape))
onBeforeUnmount(() => window.removeEventListener('keydown', handleEscape))

watch(() => route.path, () => {
  closeMenu()
})

const currentYear = new Date().getFullYear()
</script>

<template>
  <div class="marketing-shell min-h-screen bg-white text-slate-900 antialiased">
    <a href="#contenu" class="sr-only z-[100] rounded-lg bg-white px-4 py-2 text-sky-blue-800 focus:not-sr-only focus:fixed focus:left-4 focus:top-4">Aller au contenu</a>

    <header class="sticky top-0 z-50 border-b border-slate-200/80 bg-white/90 backdrop-blur-xl">
      <nav class="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8" aria-label="Navigation principale">
        <NuxtLink :to="MARKETING_ROUTES.home" class="flex items-center gap-2.5 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-blue-500 focus-visible:ring-offset-2">
          <Logo class="h-10 w-10 shrink-0" />
          <span class="text-lg font-bold tracking-tight text-slate-950">AppTrident</span>
        </NuxtLink>

        <div class="hidden items-center gap-7 lg:flex">
          <NuxtLink
            v-for="item in MARKETING_NAVIGATION"
            :key="item.href"
            :to="item.href"
            class="rounded text-sm font-medium text-slate-600 transition hover:text-sky-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-blue-500 focus-visible:ring-offset-4"
            active-class="!font-semibold !text-sky-blue-700"
          >
            {{ item.label }}
          </NuxtLink>
          <NuxtLink :to="`${MARKETING_ROUTES.home}#avis`" class="rounded text-sm font-medium text-slate-600 transition hover:text-sky-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-blue-500 focus-visible:ring-offset-4">Avis</NuxtLink>
          <NuxtLink :to="`${MARKETING_ROUTES.home}#tarif`" class="rounded text-sm font-medium text-slate-600 transition hover:text-sky-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-blue-500 focus-visible:ring-offset-4">Tarif</NuxtLink>
        </div>

        <div class="hidden items-center gap-4 lg:flex">
          <NuxtLink to="/login" class="rounded text-sm font-semibold text-slate-600 transition hover:text-sky-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-blue-500 focus-visible:ring-offset-4">Se connecter</NuxtLink>
          <MarketingCta :href="`${MARKETING_ROUTES.home}#tarif`" label="Accéder à l’AppTrident" page="navigation" section="header" destination="anchor" />
        </div>

        <button type="button" class="flex h-11 w-11 items-center justify-center rounded-lg text-slate-700 transition-colors hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-blue-500 lg:hidden" :aria-expanded="isMenuOpen" aria-controls="marketing-mobile-menu" :aria-label="isMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'" @click="isMenuOpen = !isMenuOpen">
          <XMarkIcon v-if="isMenuOpen" class="h-6 w-6" aria-hidden="true" />
          <Bars3Icon v-else class="h-6 w-6" aria-hidden="true" />
        </button>
      </nav>

      <Transition enter-active-class="transition duration-200 ease-[cubic-bezier(0.16,1,0.3,1)]" enter-from-class="-translate-y-2 opacity-0" enter-to-class="translate-y-0 opacity-100" leave-active-class="transition duration-150 ease-out" leave-from-class="translate-y-0 opacity-100" leave-to-class="-translate-y-2 opacity-0">
      <div v-if="isMenuOpen" id="marketing-mobile-menu" class="border-t border-slate-200 bg-white px-4 py-5 shadow-lg lg:hidden">
        <div class="mx-auto flex max-w-7xl flex-col gap-1">
          <NuxtLink v-for="item in MARKETING_NAVIGATION" :key="item.href" :to="item.href" class="rounded-lg px-3 py-3 text-base font-semibold text-slate-700 hover:bg-sky-blue-50 hover:text-sky-blue-800">{{ item.label }}</NuxtLink>
          <NuxtLink :to="`${MARKETING_ROUTES.home}#avis`" class="rounded-lg px-3 py-3 text-base font-semibold text-slate-700 hover:bg-sky-blue-50">Avis</NuxtLink>
          <NuxtLink :to="`${MARKETING_ROUTES.home}#tarif`" class="rounded-lg px-3 py-3 text-base font-semibold text-slate-700 hover:bg-sky-blue-50">Tarif</NuxtLink>
          <div class="my-3 h-px bg-slate-200" />
          <NuxtLink to="/login" class="rounded-lg px-3 py-3 text-base font-semibold text-slate-700">Se connecter</NuxtLink>
          <MarketingCta :href="`${MARKETING_ROUTES.home}#tarif`" label="Accéder à l’AppTrident" page="navigation" section="mobile-menu" destination="anchor" :block="true" />
        </div>
      </div>
      </Transition>
    </header>

    <main id="contenu">
      <slot />
    </main>

    <footer class="border-t border-slate-200 bg-slate-950 text-slate-300">
      <div class="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div class="grid gap-10 md:grid-cols-[1.4fr_1fr_1fr]">
          <div class="max-w-md">
            <div class="flex items-center gap-2.5"><Logo class="h-9 w-9" /><span class="text-lg font-bold text-white">AppTrident</span></div>
            <p class="mt-4 text-sm leading-6 text-slate-400">La plateforme française qui réunit découverte, analyse, valorisation et suivi de portefeuille dans un même parcours d’investissement.</p>
          </div>
          <div>
            <p class="text-sm font-bold uppercase tracking-wider text-white">Produit</p>
            <div class="mt-4 flex flex-col gap-3 text-sm"><NuxtLink v-for="item in MARKETING_NAVIGATION" :key="item.href" :to="item.href" class="hover:text-white">{{ item.label }}</NuxtLink><NuxtLink :to="`${MARKETING_ROUTES.home}#tarif`" class="hover:text-white">Tarif</NuxtLink><NuxtLink to="/login" class="hover:text-white">Connexion membre</NuxtLink></div>
          </div>
          <div>
            <p class="text-sm font-bold uppercase tracking-wider text-white">Informations</p>
            <div class="mt-4 flex flex-col gap-3 text-sm"><a :href="MARKETING_LEGAL_LINKS.terms" target="_blank" rel="noopener noreferrer" class="hover:text-white">Conditions générales de vente</a><a :href="MARKETING_LEGAL_LINKS.privacy" target="_blank" rel="noopener noreferrer" class="hover:text-white">Politique de confidentialité</a><a :href="MARKETING_LEGAL_LINKS.legal" target="_blank" rel="noopener noreferrer" class="hover:text-white">Mentions légales</a><a :href="`mailto:${MARKETING_CONTACT_EMAIL}`" class="hover:text-white">Nous contacter</a></div>
          </div>
        </div>
        <div class="mt-10 border-t border-white/10 pt-7 text-sm leading-6 text-slate-400">
          <p>Investir en Bourse comporte un risque de perte en capital. Les données, scénarios et estimations affichés par AppTrident sont des outils d’aide à l’analyse et ne constituent ni un conseil financier personnalisé ni une promesse de performance.</p>
          <p class="mt-4">© {{ currentYear }} Trident Corp. Tous droits réservés.</p>
        </div>
      </div>
    </footer>
  </div>
</template>
